// ============================================
// AUTHENTICATION UI & LOGIC
// ============================================
import {
    supabase,
    isSupabaseConfigured,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    getSession,
    onAuthStateChange,
    checkUserProStatus,
    verifyProStatusSecure,
    getSavedProjects,
} from './supabase.js';

// Auth state
let currentUser = null;
let isLoggedIn = false;

// ============================================
// INITIALIZATION
// ============================================

/**
 * Initialize authentication
 * Call this on app startup
 */
export async function initAuth() {
    if (!isSupabaseConfigured()) {
        console.log('Supabase not configured - running in offline mode');
        updateAccountUI(null);
        return;
    }

    // Check for existing session
    const session = await getSession();
    if (session?.user) {
        currentUser = session.user;
        isLoggedIn = true;
        await onUserSignedIn(session.user);
    }

    // Listen for auth changes
    onAuthStateChange(async (event, session) => {
        console.log('Auth event:', event);

        if (event === 'SIGNED_IN' && session?.user) {
            currentUser = session.user;
            isLoggedIn = true;
            await onUserSignedIn(session.user);
        } else if (event === 'SIGNED_OUT') {
            currentUser = null;
            isLoggedIn = false;
            onUserSignedOut();
        }
    });

    updateAccountUI(currentUser);
}

/**
 * Called when user signs in
 */
async function onUserSignedIn(user) {
    console.log('User signed in:', user.email);
    updateAccountUI(user);

    // Verify Pro status securely via Edge Function (uses the session JWT)
    const proStatus = await verifyProStatusSecure();

    if (proStatus.isPro) {
        // Unlock Pro without showing toast (already purchased)
        window.isPro = true;
        localStorage.setItem('willitfit_pro', 'true');
        if (typeof window.updateProUI === 'function') {
            window.updateProUI();
        }
        console.log('Pro status verified:', proStatus.purchaseDate);
    } else if (proStatus.verified) {
        // Verified as NOT Pro - clear any spoofed localStorage
        window.isPro = false;
        localStorage.removeItem('willitfit_pro');
        if (typeof window.updateProUI === 'function') {
            window.updateProUI();
        }
    }
    // If verification failed (proStatus.verified === false), keep current state

    // Migrate localStorage saves to cloud if user just signed up
    await migrateLocalSavesToCloud();

    // Refresh save slots UI if function exists
    if (typeof window.renderSaveSlots === 'function') {
        window.renderSaveSlots();
    }
}

/**
 * Called when user signs out
 */
function onUserSignedOut() {
    console.log('User signed out');
    updateAccountUI(null);

    // Refresh save slots to show localStorage saves
    if (typeof window.renderSaveSlots === 'function') {
        window.renderSaveSlots();
    }
}

// ============================================
// UI UPDATES
// ============================================

/**
 * Update the account button and related UI
 */
function updateAccountUI(user) {
    const accountBtn = document.getElementById('accountBtn');
    if (!accountBtn) return;

    if (user) {
        accountBtn.classList.add('logged-in');
        accountBtn.textContent = '✓';
        accountBtn.title = `Signed in as ${user.email}`;
    } else {
        accountBtn.classList.remove('logged-in');
        accountBtn.textContent = '👤';
        accountBtn.title = 'Sign in or create account';
    }
}

/**
 * Handle account button click
 */
export function handleAccountClick() {
    if (isLoggedIn && currentUser) {
        // Show account menu
        document.getElementById('accountEmail').textContent = currentUser.email;

        // Show Pro status
        const proStatusEl = document.getElementById('accountProStatus');
        if (window.isPro) {
            proStatusEl.innerHTML = '<span style="color: var(--success); font-weight: 600;">✓ Pro Member</span>';
        } else {
            proStatusEl.innerHTML = '<span style="color: var(--text-muted);">Free Plan</span>';
        }

        showModal('accountMenuModal');
    } else {
        // Show sign in modal
        showModal('signInModal');
    }
}

// ============================================
// AUTH HANDLERS
// ============================================

/**
 * Handle sign in form submission
 */
export async function handleSignIn(event) {
    event.preventDefault();

    const email = document.getElementById('signInEmail').value;
    const password = document.getElementById('signInPassword').value;
    const errorEl = document.getElementById('signInError');
    const button = document.getElementById('signInButton');

    errorEl.style.display = 'none';
    button.disabled = true;
    button.textContent = 'Signing in...';

    try {
        await signIn(email, password);
        closeModal('signInModal');
        showToast('Welcome back!', 'success');

        // Clear form
        document.getElementById('signInForm').reset();
    } catch (error) {
        console.error('Sign in error:', error);
        errorEl.textContent = error.message || 'Failed to sign in. Please check your credentials.';
        errorEl.style.display = 'block';
    } finally {
        button.disabled = false;
        button.textContent = 'Sign In';
    }
}

/**
 * Handle sign up form submission
 */
export async function handleSignUp(event) {
    event.preventDefault();

    const email = document.getElementById('signUpEmail').value;
    const password = document.getElementById('signUpPassword').value;
    const errorEl = document.getElementById('signUpError');
    const button = document.getElementById('signUpButton');

    errorEl.style.display = 'none';
    button.disabled = true;
    button.textContent = 'Creating account...';

    try {
        await signUp(email, password);
        closeModal('signUpModal');
        showToast('Account created! Check your email to confirm.', 'success');

        // Clear form
        document.getElementById('signUpForm').reset();
    } catch (error) {
        console.error('Sign up error:', error);
        errorEl.textContent = error.message || 'Failed to create account. Please try again.';
        errorEl.style.display = 'block';
    } finally {
        button.disabled = false;
        button.textContent = 'Create Account';
    }
}

/**
 * Handle sign out
 */
export async function handleSignOut() {
    try {
        await signOut();
        closeModal('accountMenuModal');
        showToast('Signed out successfully', 'success');
    } catch (error) {
        console.error('Sign out error:', error);
        showToast('Failed to sign out', 'error');
    }
}

// ============================================
// SMART ACCOUNT PROMPTS
// ============================================

// Track if we've shown the account prompt this session
let accountPromptShown = false;
let accountPromptDismissed = localStorage.getItem('willitfit_prompt_dismissed') === 'true';

/**
 * Check if we should show account prompt
 * Call this when user completes a project or tries to use second save slot
 */
export function maybeShowAccountPrompt(reason = 'save') {
    // Don't show if already logged in
    if (isLoggedIn) return false;

    // Don't show if already shown this session or permanently dismissed
    if (accountPromptShown || accountPromptDismissed) return false;

    // Don't show if Supabase isn't configured
    if (!isSupabaseConfigured()) return false;

    accountPromptShown = true;
    showModal('accountPromptModal');
    return true;
}

/**
 * Dismiss the account prompt (user chose to continue as guest)
 */
export function dismissAccountPrompt() {
    // Don't show again this session
    accountPromptShown = true;
    // Optionally persist this choice
    // localStorage.setItem('willitfit_prompt_dismissed', 'true');
}

/**
 * Check if user should be prompted to create account
 * Call this after significant actions like filling container 80%+
 */
export function checkAccountPromptTrigger(usedPercent) {
    // Trigger at 80% container usage
    if (usedPercent >= 80 && !isLoggedIn && !accountPromptShown && !accountPromptDismissed) {
        maybeShowAccountPrompt('progress');
    }
}

// ============================================
// DATA MIGRATION
// ============================================

/**
 * Migrate localStorage saves to cloud when user signs up
 */
async function migrateLocalSavesToCloud() {
    if (!isLoggedIn || !currentUser) return;

    // Check if we've already migrated
    if (localStorage.getItem('willitfit_migrated_' + currentUser.id)) return;

    // Get existing cloud saves
    const cloudSaves = await getSavedProjects();
    const usedSlots = new Set(cloudSaves.map(s => s.slot));

    // Check localStorage for saves
    for (let slot = 0; slot < 3; slot++) {
        const localSave = localStorage.getItem(`willitfit_save_${slot}`);
        if (localSave && !usedSlots.has(slot)) {
            try {
                const saveData = JSON.parse(localSave);
                // Import to cloud using the supabase saveProject function
                const { saveProject } = await import('./supabase.js');
                await saveProject(slot, saveData);
                console.log(`Migrated local save slot ${slot} to cloud`);
            } catch (e) {
                console.error(`Failed to migrate slot ${slot}:`, e);
            }
        }
    }

    // Mark as migrated
    localStorage.setItem('willitfit_migrated_' + currentUser.id, 'true');
}

// ============================================
// HELPERS
// ============================================

/**
 * Get current auth state
 */
export function getAuthState() {
    return {
        isLoggedIn,
        user: currentUser,
    };
}

/**
 * Check if user is logged in
 */
export function isUserLoggedIn() {
    return isLoggedIn;
}

/**
 * Get current user
 */
export function getUser() {
    return currentUser;
}

// Helper functions that should be available globally
function showModal(id) {
    document.getElementById(id)?.classList.add('show');
}

function closeModal(id) {
    document.getElementById(id)?.classList.remove('show');
}

function showToast(message, type = 'success') {
    if (typeof window.showToast === 'function') {
        window.showToast(message, type);
    } else {
        console.log(`Toast (${type}): ${message}`);
    }
}

// Export modal helpers for use in HTML onclick handlers
window.handleAccountClick = handleAccountClick;
window.handleSignIn = handleSignIn;
window.handleSignUp = handleSignUp;
window.handleSignOut = handleSignOut;
window.dismissAccountPrompt = dismissAccountPrompt;
